import { ColumnSort } from "@tanstack/react-table";
import { createContext, ReactNode, useContext } from "react";

import {
  Client,
  TradingGraphs,
  TradingReportStatsOutputBody,
} from "../../../api/generated";
import {
  ClientPageAnalyticsContextProps,
  ClientPageAnalyticsProvider,
} from "../../Common/providers/ClientPageAnalyticsProvider";

type PaginationMetadata = {
  totalCount?: number;
  currentPage?: number;
  pageSize?: number;
};

type ClientTradingReportProviderProps = {
  children: ReactNode;
} & TradingListContextProps &
  GraphsDataContextProps &
  ClientPageAnalyticsContextProps;

/**
 * entry point for the ClientTradingTableProvider wraps all context provider into one
 * @returns ReactNode
 */
export const ClientTradingReportProvider = ({
  children,
  tradingList,
  redactedFields,
  graphsData,
  instrumentBarChartData,
  startDate,
  endDate,
  analytics,
  metadata,
  onPageChange,
  isServerSidePagination = false,
  setSorting,
}: ClientTradingReportProviderProps) => {
  return (
    <ClientPageAnalyticsProvider analytics={analytics}>
      <TradingListProvider
        tradingList={tradingList}
        startDate={startDate}
        endDate={endDate}
        redactedFields={redactedFields}
        metadata={metadata}
        onPageChange={onPageChange}
        setSorting={setSorting}
        isServerSidePagination={isServerSidePagination}
      >
        <GraphsDataProvider
          graphsData={graphsData}
          instrumentBarChartData={instrumentBarChartData}
        >
          {children}
        </GraphsDataProvider>
      </TradingListProvider>
    </ClientPageAnalyticsProvider>
  );
};

type TradingListContextProps = {
  tradingList: Client[];
  redactedFields?: string[];
  startDate?: string;
  endDate?: string;
  metadata?: PaginationMetadata;
  onPageChange?: (page: number) => void;
  setSorting?: (sort: ColumnSort) => void;
  isServerSidePagination?: boolean;
};

// TradingList Context
const TradingListContext = createContext<TradingListContextProps>({
  tradingList: [],
  isServerSidePagination: false,
});

const TradingListProvider = ({
  children,
  ...props
}: {
  children: ReactNode;
} & TradingListContextProps) => (
  <TradingListContext.Provider
    value={{
      ...props,
    }}
  >
    {children}
  </TradingListContext.Provider>
);

export const useTradingListContext = () => useContext(TradingListContext);

type GraphsDataContextProps = {
  graphsData?: TradingGraphs;
  instrumentBarChartData?: TradingReportStatsOutputBody["instrumentsData"];
};

// GraphsData Context
const GraphsDataContext = createContext<GraphsDataContextProps>({});
const GraphsDataProvider = ({
  children,
  graphsData,
  instrumentBarChartData,
}: {
  children: ReactNode;
} & GraphsDataContextProps) => (
  <GraphsDataContext.Provider value={{ graphsData, instrumentBarChartData }}>
    {children}
  </GraphsDataContext.Provider>
);
export const useGraphsDataContext = () => useContext(GraphsDataContext);
